// @ts-nocheck
import { Tip, escapeHtml, scheduleTipMove } from "./net-ui";
import { SPELLS } from "./data";
import { Game } from "./engine/game-base";

/** Display metadata for timed buff/debuff HUD chips. */
export const STATUS_FX_META = {
  stun:    { icon: "💫", tip: "Stun", debuff: true },
  root:    { icon: "🌱", tip: "Root", debuff: true },
  silence: { icon: "🔇", tip: "Silence", debuff: true },
  freeze:  { icon: "🧊", tip: "Freeze", debuff: true },
  slow:    { icon: "❄️", tip: "Slow", debuff: true },
  burn:    { icon: "🔥", tip: "Burn", debuff: true },
  hemorrhage: { icon: "🩸", tip: "Hemorrhage", debuff: true },
  exhaustion: { icon: "😮‍💨", tip: "Exhaustion", debuff: true },
  mindlash:   { icon: "🧠", tip: "Mind Lash", debuff: true },
  mindctrl:   { icon: "👁‍🗨", tip: "Dominated", debuff: true },
  contagion:  { icon: "🦠", tip: "Contagion", debuff: true },
  essence_light: { icon: "☀️", tip: "Light Attuned", debuff: false },
  essence_void:  { icon: "🌑", tip: "Void Attuned", debuff: false },
  tether:  { icon: "⛓️", tip: "Tether", debuff: true },
  channel: { icon: "⏳", tip: "Channel", debuff: true },
  invis:   { icon: "🌫️", tip: "Invisible", debuff: false },
  rush:    { icon: "💨", tip: "Rush", debuff: false },
  shield:  { icon: "🛡️", tip: "Shield", debuff: false },
  barrier: { icon: "🔮", tip: "Barrier", debuff: false },
  reflect: { icon: "↩️", tip: "Reflect", debuff: false },
  timeshift: { icon: "⏳", tip: "Time Shift", debuff: false },
};

export function formatStatusFxTime(rem) {
  if (rem >= 10) return String(Math.ceil(rem));
  if (rem >= 1) return rem.toFixed(1);
  return rem.toFixed(1);
}

export function statusFxKey(fx) {
  return `${fx.id}|${fx.spell || ""}|${fx.buff ? "b" : "d"}`;
}

export function statusFxTipText(fx) {
  if (fx.id === "slow" && fx.val > 0 && fx.val < 1) {
    return `${Math.round((1 - fx.val) * 100)}% slow`;
  }
  if (fx.id === "exhaustion" && fx.val > 0) {
    return `Exhaustion · +${fx.val}s cast CD`;
  }
  if (fx.id === "channel" && fx.spell && SPELLS[fx.spell]) {
    return `Channel · ${SPELLS[fx.spell].name}`;
  }
  if (fx.id === "essence_light") {
    return "Light attuned · immune to Morwenna · vulnerable to Lys · refresh in gold ward";
  }
  if (fx.id === "essence_void") {
    return "Void attuned · immune to Lys · vulnerable to Morwenna · refresh in void ward";
  }
  if (fx.id === "contagion") {
    return "Contagion · ticks damage; spreads to allies within 140 every 0.5s";
  }
  if (fx.id === "timeshift") {
    return "Time Shift · rewinding";
  }
  return STATUS_FX_META[fx.id]?.tip || fx.id;
}

function statusFxIcon(fx) {
  if ((fx.id === "channel" || fx.id === "timeshift") && fx.spell && SPELLS[fx.spell]) {
    return SPELLS[fx.spell].fallbackIcon || STATUS_FX_META[fx.id]?.icon || "•";
  }
  return STATUS_FX_META[fx.id]?.icon || "•";
}

function statusFxChipClass(fx) {
  let cls = "status-fx-chip " + (fx.buff ? "is-buff" : "is-debuff");
  if (fx.id === "essence_light") cls += " is-essence-light";
  if (fx.id === "essence_void") cls += " is-essence-void";
  return cls;
}

function statusFxRem(fx, now) {
  return Math.max(0, fx.rem - (now - fx.at) / 1000);
}

function createStatusFxChip(fx, key) {
  const chip = document.createElement("div");
  chip.className = statusFxChipClass(fx);
  chip.dataset.fxKey = key;
  chip.dataset.tipLabel = statusFxTipText(fx);
  const icon = document.createElement("div");
  icon.className = "status-fx-icon";
  icon.textContent = statusFxIcon(fx);
  const time = document.createElement("div");
  time.className = "status-fx-time";
  chip.appendChild(icon);
  chip.appendChild(time);
  return chip;
}

function syncStatusFxChip(chip, fx, now) {
  const remLeft = statusFxRem(fx, now);
  chip.className = statusFxChipClass(fx);
  chip.classList.toggle("is-expiring", remLeft < 2);
  chip.dataset.tipLabel = statusFxTipText(fx);
  const time = chip.querySelector(".status-fx-time");
  if (time) time.textContent = formatStatusFxTime(remLeft);
}

function clearStatusFxTip(game) {
  if (!game.statusTipKey) return;
  game.statusTipKey = null;
  Tip.hide();
}

function statusFxSubject(game) {
  const playing = game.state === "playing" || game.state === "countdown";
  if (!playing) return null;
  if (game.inSpectateView?.()) {
    const w = game.spectateTarget?.();
    if (!w || !w.alive || w.falling) return null;
    return game._watchEffects || [];
  }
  const p = game.human?.();
  if (!p || !p.alive || p.falling) return null;
  return game._myEffects || [];
}

/** @param {import("./engine/game-base").Game} game */
export function updateStatusFxHud(game) {
  const el = document.getElementById("statusFxHud");
  if (!el) return;
  const effects = statusFxSubject(game);
  const show = effects != null;
  if (show && typeof game.spellBarLayout === "function") {
    const lay = game.spellBarLayout();
    el.style.bottom = `${game.H - lay.yTop + lay.hintGap}px`;
  }
  if (!show) {
    el.classList.add("hidden");
    el.replaceChildren();
    clearStatusFxTip(game);
    return;
  }
  const now = performance.now();
  const live = effects
    .map(fx => ({ ...fx, remLeft: statusFxRem(fx, now) }))
    .filter(fx => fx.remLeft > 0.05)
    .sort((a, b) => {
      const da = a.buff ? 1 : 0, db = b.buff ? 1 : 0;
      if (da !== db) return da - db;
      return a.remLeft - b.remLeft;
    });
  if (!live.length) {
    el.classList.add("hidden");
    el.replaceChildren();
    clearStatusFxTip(game);
    return;
  }
  el.classList.remove("hidden");
  const liveKeys = new Set();
  for (const fx of live) {
    const key = statusFxKey(fx);
    liveKeys.add(key);
    let chip = el.querySelector(`[data-fx-key="${key}"]`);
    if (!chip) {
      chip = createStatusFxChip(fx, key);
      el.appendChild(chip);
    }
    syncStatusFxChip(chip, fx, now);
  }
  for (const chip of [...el.children]) {
    if (!liveKeys.has(chip.dataset.fxKey)) chip.remove();
  }
}

function statusFxTipHTML(label) {
  return `<div class="tip-desc">${escapeHtml(label)}</div>`;
}

const STATUS_TIP_OPTS = { preferAbove: true };

/** Same cursor-anchored tooltip path as the on-canvas spell bar. */
Game.prototype.updateStatusFxHover = function (x, y) {
  const hud = document.getElementById("statusFxHud");
  if (!hud || hud.classList.contains("hidden")) {
    if (this.statusTipKey) this.statusTipKey = null;
    return false;
  }
  const chip = document.elementFromPoint(x, y)?.closest?.(".status-fx-chip");
  if (!chip || !hud.contains(chip)) {
    if (this.statusTipKey && Tip.isOver(x, y)) return true;
    if (this.statusTipKey) this.statusTipKey = null;
    return false;
  }
  const label = chip.dataset.tipLabel || "";
  const key = `${chip.dataset.fxKey}|${label}`;
  const r = chip.getBoundingClientRect();
  const ax = x;
  const ay = r.top;
  if (key !== this.statusTipKey) {
    this.statusTipKey = key;
    Tip.show(statusFxTipHTML(label), ax, ay, STATUS_TIP_OPTS);
  } else {
    scheduleTipMove(ax, ay, STATUS_TIP_OPTS);
  }
  return true;
};
